<template>
  <div class="card border-0 shadow-none rounded-3 mb-4">
    <div class="card-body p-4 p-xl-40">
      <div class="toolbar-card mb-4">
        <div class="toolbar-left">
          <h4 class="mb-1">{{ $t("currency_page.list_title") }}</h4>
          <p class="text-muted mb-0">
            {{ $t("currency_page.list_description") }}
          </p>
        </div>
        <div class="toolbar-right">
          <div class="search-shell">
            <input
              :value="searchQuery"
              type="text"
              class="form-control toolbar-search"
              :placeholder="$t('currency_page.search_placeholder')"
              @input="$emit('update:searchQuery', ($event.target as HTMLInputElement).value)"
            />
            <i class="ri-search-line search-icon"></i>
          </div>
          <button class="btn style-one btn-sm d-flex align-items-center gap-2" @click="$emit('create')">
            <i class="ri-add-circle-line"></i>
            <span>{{ $t("currency_page.create_button") }}</span>
          </button>
        </div>
      </div>

      <a-table
        :columns="columns"
        :data-source="currencies"
        :row-key="'id'"
        :pagination="paginationConfig"
        :locale="{ emptyText: $t('currency_page.no_currencies_found') }"
        class="currency-table"
      >
        <template #bodyCell="{ column, record, text }">
          <template v-if="column.key === 'code'">
            <span class="code-pill">{{ text || $t("currency_page.not_available") }}</span>
          </template>
          <template v-else-if="column.key === 'symbol'">
            <span class="symbol-pill">{{ text || $t("currency_page.empty_symbol") }}</span>
          </template>
          <template v-else-if="column.key === 'rate'">
            {{ formatRate(record.rate) }}
          </template>
          <template v-else-if="column.key === 'created_by'">
            {{ text || $t("currency_page.system") }}
          </template>
          <template v-else-if="column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2">
              <button class="icon-btn" :title="$t('currency_page.edit')" @click="$emit('edit', record)">
                <i class="ri-pencil-line"></i>
              </button>
              <button class="icon-btn danger" :title="$t('currency_page.delete')" @click="$emit('delete', record)">
                <i class="ri-delete-bin-6-line"></i>
              </button>
            </div>
          </template>
          <template v-else>
            {{ text || $t("currency_page.not_available") }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { Currency } from "@/services/currency";

export default defineComponent({
  name: "CurrencyList",
  emits: ["update:searchQuery", "create", "edit", "delete"],
  props: {
    currencies: {
      type: Array as PropType<Currency[]>,
      required: true,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    paginationConfig: {
      type: Object as PropType<Record<string, unknown>>,
      required: true,
    },
  },
  setup() {
    const { t } = useI18n();
    const columns = computed(() => [
      { title: t("currency_page.table_code"), dataIndex: "code", key: "code", align: "center" as const, width: 160 },
      { title: t("currency_page.table_name"), dataIndex: "name", key: "name", align: "center" as const, width: 240 },
      { title: t("currency_page.table_symbol"), dataIndex: "symbol", key: "symbol", align: "center" as const, width: 140 },
      { title: t("currency_page.table_rate"), dataIndex: "rate", key: "rate", align: "center" as const, width: 160 },
      { title: t("currency_page.table_created_by"), dataIndex: "created_by", key: "created_by", align: "center" as const, width: 160 },
      { title: t("currency_page.table_action"), key: "actions", align: "center" as const, width: 140 },
    ]);

    const formatRate = (rate: number | string) => Number(rate || 0).toFixed(4);

    return {
      columns,
      formatRate,
    };
  },
});
</script>

<style scoped>
.toolbar-card {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 18px;
  padding: 22px;
  border: 1px solid #edf0f7;
  border-radius: 20px;
  background: #fbfcff;
}

.toolbar-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.search-shell {
  position: relative;
  min-width: 340px;
}

.toolbar-search {
  min-height: 54px;
  padding: 14px 48px 14px 18px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  box-shadow: none;
}

.toolbar-search:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.search-icon {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #7d8aa2;
  pointer-events: none;
}

.currency-table :deep(.ant-table-thead > tr > th) {
  font-weight: 700;
}

.code-pill,
.symbol-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 700;
}

.code-pill {
  background: #eef2ff;
  color: #4f46e5;
}

.symbol-pill {
  background: #f3f4f6;
  color: #334155;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e3e8f5;
  background: #fff;
  color: #4f46e5;
}

.icon-btn.danger {
  color: #d33c43;
}
</style>
